export interface CacheData<T> {
  timestamp: number;
  user: string;
  data: T;
}

export const setCache = <T>(key: string, data: T, user: string): void => {
  const cache: CacheData<T> = {
    timestamp: Date.now(),
    user,
    data,
  };
  // Store with user-specific key to match removal pattern in login.tsx
  const cacheKey = `${key}_${user}`;
  localStorage.setItem(cacheKey, JSON.stringify(cache));
};

/** Clears customer form location lookups (zones for customer form, countries, states, city/zip search cache). */
export const clearCustomerLocationLookupCaches = (user: string): void => {
  localStorage.removeItem(`customerZones_${user}`);
  localStorage.removeItem(`countries_${user}`);
  localStorage.removeItem(`states_${user}`);
};

/** Keys used by useLookupData / technician calendar (see fetchCatch updateLookup). Cleared on each login so scheduling data is always refetched. */
const SCHEDULING_LOOKUP_CACHE_KEYS = [
  "sch_locationBusinessHours",
  "sch_locationHolidays",
  "sch_employeeSchedules",
  "sch_employeeScheduleExceptions",
] as const;

export const clearSchedulingLookupCaches = (user: string): void => {
  for (const key of SCHEDULING_LOOKUP_CACHE_KEYS) {
    localStorage.removeItem(`${key}_${user}`);
  }
};

export const getCache = <T>(
  key: string,
  user: string,
  maxAge: number,
): T | null => {
  // Use user-specific key to match storage pattern
  const cacheKey = `${key}_${user}`;
  const cacheString = localStorage.getItem(cacheKey);
  if (!cacheString) return null;

  try {
    const cache: CacheData<T> = JSON.parse(cacheString);
    const isExpired = Date.now() - cache.timestamp > maxAge;
    const isDifferentUser = cache.user !== user;

    if (isExpired || isDifferentUser) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return cache.data;
  } catch (error) {
    // If cache is corrupted, remove it
    console.warn(`Failed to parse cache for ${cacheKey}:`, error);
    localStorage.removeItem(cacheKey);
    return null;
  }
};

// Image Cache Service for Customer Profile Images
interface CacheEntry {
  blobUrl?: string; // Blob URL for displaying the image (not persisted)
  timestamp: number; // Cache creation timestamp
  dataUrl: string; // Data URL for localStorage persistence
  notFound?: boolean; // Flag for 404 responses
}

const CACHE_PREFIX = "customer-image-";
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours
const MAX_CACHE_SIZE = 10 * 1024 * 1024; // 10MB

class ImageCacheService {
  private cache = new Map<string, CacheEntry>();
  private pendingRequests = new Map<string, Promise<string | null>>();

  /**
   * Get customer image URL from cache or fetch if not cached
   */
  async getCustomerImage(
    customerIdentity: number,
    fetchImageFn: () => Promise<Blob>,
  ): Promise<string | null> {
    const cacheKey = `${CACHE_PREFIX}${customerIdentity}`;

    // Check if request is already pending
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey)!;
    }

    // Create pending request
    const requestPromise = this.fetchAndCache(
      customerIdentity,
      cacheKey,
      fetchImageFn,
    );
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  /**
   * Fetch image and cache the result
   */
  private async fetchAndCache(
    customerIdentity: number,
    cacheKey: string,
    fetchImageFn: () => Promise<Blob>,
  ): Promise<string | null> {
    // Check memory cache first
    const memoryEntry = this.cache.get(cacheKey);
    if (memoryEntry && this.isValidEntry(memoryEntry)) {
      return memoryEntry.dataUrl;
    }

    // Check localStorage
    const localStorageEntry = this.getFromLocalStorage(cacheKey);
    if (localStorageEntry && this.isValidEntry(localStorageEntry)) {
      // Restore to memory cache
      this.cache.set(cacheKey, localStorageEntry);
      return localStorageEntry.dataUrl;
    }

    // If cached as not found, return null immediately
    if (localStorageEntry?.notFound || memoryEntry?.notFound) {
      return null;
    }

    // Fetch from API
    try {
      const blob = await fetchImageFn();

      if (blob && blob.size > 0) {
        const dataUrl = await this.blobToDataUrl(blob);
        const entry: CacheEntry = {
          timestamp: Date.now(),
          dataUrl,
          notFound: false,
        };

        this.cache.set(cacheKey, entry);
        this.saveToLocalStorage(cacheKey, entry);
        return dataUrl;
      } else {
        // Empty blob - treat as not found
        this.cacheNotFound(customerIdentity, cacheKey);
        return null;
      }
    } catch (error: any) {
      // Handle 404 or other errors
      if (error?.response?.status === 404 || error?.status === 404) {
        this.cacheNotFound(customerIdentity, cacheKey);
        return null;
      }

      console.error(
        `Error fetching image for customer ${customerIdentity}:`,
        error,
      );
      return null;
    }
  }

  /**
   * Cache 404 responses to prevent repeated requests
   */
  private cacheNotFound(customerIdentity: number, cacheKey: string): void {
    const entry: CacheEntry = {
      timestamp: Date.now(),
      dataUrl: "",
      notFound: true,
    };

    this.cache.set(cacheKey, entry);
    this.saveToLocalStorage(cacheKey, entry);
  }

  /**
   * Check if cache entry is still valid (not expired)
   */
  private isValidEntry(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp < CACHE_EXPIRY_MS;
  }

  /**
   * Get entry from localStorage
   */
  private getFromLocalStorage(cacheKey: string): CacheEntry | null {
    try {
      const stored = localStorage.getItem(cacheKey);
      if (!stored) return null;

      const entry: CacheEntry = JSON.parse(stored);
      return entry;
    } catch (error) {
      console.warn(`Failed to read from localStorage for ${cacheKey}:`, error);
      return null;
    }
  }

  /**
   * Save entry to localStorage with size management
   */
  private saveToLocalStorage(cacheKey: string, entry: CacheEntry): void {
    try {
      const entrySize = this.estimateEntrySize(entry);
      const currentSize = this.getLocalStorageSize();

      // Cleanup if approaching size limit
      if (currentSize + entrySize > MAX_CACHE_SIZE) {
        this.cleanupLocalStorage();
      }

      localStorage.setItem(cacheKey, JSON.stringify(entry));
    } catch (error: any) {
      // Handle quota exceeded errors
      if (error.name === "QuotaExceededError" || error.code === 22) {
        console.warn("localStorage quota exceeded, cleaning up cache");
        this.cleanupLocalStorage();
        try {
          localStorage.setItem(cacheKey, JSON.stringify(entry));
        } catch (retryError) {
          console.warn(`Failed to save ${cacheKey} after cleanup:`, retryError);
        }
      } else {
        console.warn(`Failed to save to localStorage for ${cacheKey}:`, error);
      }
    }
  }

  /**
   * Estimate size of cache entry in bytes
   */
  private estimateEntrySize(entry: CacheEntry): number {
    // Rough estimate: JSON string length + data URL size
    const jsonSize = JSON.stringify(entry).length;
    const dataUrlSize = entry.dataUrl ? entry.dataUrl.length : 0;
    return jsonSize + dataUrlSize;
  }

  /**
   * Get current localStorage size in bytes
   */
  private getLocalStorageSize(): number {
    let total = 0;
    for (const key in localStorage) {
      if (key.startsWith(CACHE_PREFIX)) {
        const value = localStorage.getItem(key);
        if (value) {
          total += key.length + value.length;
        }
      }
    }
    return total;
  }

  /**
   * Cleanup localStorage by removing oldest entries until under 80% of limit
   */
  private cleanupLocalStorage(): void {
    const entries: Array<{ key: string; entry: CacheEntry }> = [];

    // Collect all cache entries
    for (const key in localStorage) {
      if (key.startsWith(CACHE_PREFIX)) {
        try {
          const stored = localStorage.getItem(key);
          if (stored) {
            const entry: CacheEntry = JSON.parse(stored);
            entries.push({ key, entry });
          }
        } catch (error) {
          // Remove invalid entries
          localStorage.removeItem(key);
        }
      }
    }

    // Sort by timestamp (oldest first)
    entries.sort((a, b) => a.entry.timestamp - b.entry.timestamp);

    // Remove entries until under 80% of limit
    const targetSize = MAX_CACHE_SIZE * 0.8;
    let currentSize = this.getLocalStorageSize();

    for (const { key } of entries) {
      if (currentSize <= targetSize) break;

      const value = localStorage.getItem(key);
      if (value) {
        currentSize -= key.length + value.length;
        localStorage.removeItem(key);
        this.cache.delete(key);
      }
    }
  }

  /**
   * Convert blob to data URL
   */
  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () =>
        reject(new Error("Failed to convert blob to data URL"));
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Clear all cached images
   */
  clearCache(): void {
    this.cache.clear();
    this.pendingRequests.clear();

    // Clear from localStorage
    for (const key in localStorage) {
      if (key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  }

  /**
   * Clear cache for specific customer
   */
  clearCustomerImage(customerIdentity: number): void {
    const cacheKey = `${CACHE_PREFIX}${customerIdentity}`;
    this.cache.delete(cacheKey);
    this.pendingRequests.delete(cacheKey);
    localStorage.removeItem(cacheKey);
  }
}

// Export singleton instance
export const imageCacheService = new ImageCacheService();

// CACHE utility FUNCTIONS DISABLED - No longer using cache
// export const clearImageCache = () => {
//   imageCacheService.clearAll();
// };

// export const clearCustomerImageCache = (customerIdentity: number) => {
//   imageCacheService.clearCustomerImage(customerIdentity);
// };

// export const getImageCacheStats = () => {
//   return imageCacheService.getStats();
// };

// export const isCustomerImageCached = (customerIdentity: number) => {
//   return imageCacheService.isCustomerImageCached(customerIdentity);
// };

// export const getCachedCustomerImage = (customerIdentity: number) => {
//   return imageCacheService.getCachedCustomerImage(customerIdentity);
// };

// export const logCacheStats = () => {
//   const stats = imageCacheService.getStats();
//   console.log('Image Cache Statistics:', stats);
//   return stats;
// };

// CACHE CLEANUP DISABLED - No longer using cache
// export const setupImageCacheCleanup = () => {
//   // Clear cache every 30 minutes to prevent memory buildup
//   setInterval(() => {
//     const stats = imageCacheService.getStats();
//     if (stats.cachedImages > 100) {
//       console.log('Clearing image cache due to high usage:', stats);
//       imageCacheService.clearAll();
//     }
//   }, 30 * 60 * 1000); // 30 minutes
// };
